import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Area, ComposedChart, CartesianGrid, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, num, rupee } from "@/lib/api";
import { useTheme } from "@/lib/theme";

const ncdf = (x) => { const t = 1 / (1 + 0.2316419 * Math.abs(x)); const d = 0.3989423 * Math.exp((-x * x) / 2);
  const p = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274)))); return x > 0 ? 1 - p : p; };
const bs = (S, K, T, iv, typ) => {
  if (T <= 0 || iv <= 0) return typ === "CE" ? Math.max(S - K, 0) : Math.max(K - S, 0);
  const sd = iv * Math.sqrt(T), d1 = (Math.log(S / K) + 0.5 * sd * sd) / sd, d2 = d1 - sd;
  return typ === "CE" ? S * ncdf(d1) - K * ncdf(d2) : K * ncdf(-d2) - S * ncdf(-d1);
};
const legIv = (l, ivs) => {
  const v = Number(ivs?.iv?.[String(Math.round(l.strike))]?.[l.type.toLowerCase()]);
  return (v > 0 ? v : ivs?.fallback_iv || 12) / 100;
};
export const todayAt = (legs, S, qty, ivs) => legs.reduce((a, l) => a + (l.side === "BUY" ? 1 : -1)
  * ((l.type === "FUT" ? S : bs(S, l.strike, ivs.T, legIv(l, ivs), l.type)) - l.entry), 0) * qty;

const intrinsic = (l, S) => (l.type === "FUT" ? S : l.type === "CE" ? Math.max(S - l.strike, 0) : Math.max(l.strike - S, 0));

export const payoffLegs = (t) => {
  if (t.legs) return t.legs.map((l) => ({ side: l.side, type: l.type, strike: l.strike, entry: Number(l.entry) || 0 }));
  if (t.buy_strike == null) return [];
  const bp = t.entry_long ?? t.buy_price ?? t.debit;
  const sp = t.entry_long != null ? t.entry_short : t.buy_price != null ? t.sell_price : 0;
  return [{ side: "BUY", type: t.opt_type, strike: t.buy_strike, entry: Number(bp) || 0 },
    { side: "SELL", type: t.opt_type, strike: t.sell_strike, entry: Number(sp) || 0 }];
};

export const payoffAt = (legs, S, qty) => legs.reduce((a, l) => a + (l.side === "BUY" ? 1 : -1) * (intrinsic(l, S) - l.entry), 0) * qty;

const curve = (legs, qty, spot) => {
  const ks = legs.filter((l) => l.strike != null).map((l) => l.strike).concat(spot ? [spot] : []);
  const lo = Math.min(...ks), hi = Math.max(...ks);
  const pad = Math.max((hi - lo) * 0.6, (spot || lo) * 0.03);
  const a = lo - pad, b = hi + pad, n = 140;
  const xs = Array.from({ length: n + 1 }, (_, i) => a + ((b - a) * i) / n).concat(ks).sort((x, y) => x - y);
  const pts = xs.map((S) => ({ S: Math.round(S), pnl: Math.round(payoffAt(legs, S, qty)) }));
  const bes = [];
  for (let i = 1; i < pts.length; i++) {
    const p = pts[i - 1], q = pts[i];
    if ((p.pnl < 0) !== (q.pnl < 0) && q.pnl !== p.pnl) bes.push(Math.round(p.S + ((q.S - p.S) * -p.pnl) / (q.pnl - p.pnl)));
  }
  return { pts, bes };
};

export const PayoffChart = ({ trade, spot, id = "payoff-chart", height = 180 }) => {
  const legs = useMemo(() => payoffLegs(trade), [trade]);
  const qty = trade.qty || (trade.lot_size || 0) * (trade.lots || 1);
  const [ivs, setIvs] = useState(null);
  const light = useTheme() === "light";
  useEffect(() => {
    if (!trade.expiry) return;
    api.get("/payoff/iv", { params: { expiry: trade.expiry } }).then((r) => setIvs(r.data)).catch(() => setIvs(null));
  }, [trade.expiry]);
  const base = useMemo(() => (legs.length ? curve(legs, qty, spot || trade.spot) : { pts: [], bes: [] }), [legs, qty, spot, trade.spot]);
  const pts = useMemo(() => (ivs ? base.pts.map((p) => ({ ...p, today: Math.round(todayAt(legs, p.S, qty, ivs)) })) : base.pts), [base, ivs, legs, qty]);
  const { bes } = base;
  if (!pts.length || !qty) return <p className="text-xs text-slate-500" data-testid={`${id}-empty`}>No legs to chart.</p>;
  const max = Math.max(...pts.map((p) => p.pnl)), min = Math.min(...pts.map((p) => p.pnl));
  const off = max <= 0 ? 0 : min >= 0 ? 1 : max / (max - min);
  const gid = `pg-${id}`;
  const now = spot || ivs?.spot || trade.spot;
  return (
    <div data-testid={id} className="fade-in">
      <div className="mb-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-slate-400">
        <span>At expiry · range max <b className="up" data-testid={`${id}-max`}>{rupee(max)}</b></span>
        <span>min <b className="down" data-testid={`${id}-min`}>{rupee(min)}</b></span>
        <span>breakeven <b className="text-slate-200" data-testid={`${id}-be`}>{bes.length ? bes.map((x) => num(x, 0)).join(" / ") : "—"}</b></span>
        {now && <span>now <b className="text-indigo-300">{num(now, 0)}</b> → <b className={payoffAt(legs, now, qty) >= 0 ? "up" : "down"}>{rupee(payoffAt(legs, now, qty))}</b></span>}
        {ivs && now && (
          <span data-testid={`${id}-today`}><span className="mr-1 inline-block h-0.5 w-3 bg-amber-400 align-middle" />today (BS, chain IV, {num(ivs.T * 252, 1)} session{ivs.T * 252 > 1.05 ? "s" : ""} left) → <b className={todayAt(legs, now, qty, ivs) >= 0 ? "up" : "down"}>{rupee(todayAt(legs, now, qty, ivs))}</b></span>
        )}
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={pts} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
              <stop offset={0} stopColor="#34d399" stopOpacity={0.45} />
              <stop offset={off} stopColor="#34d399" stopOpacity={0.08} />
              <stop offset={off} stopColor="#f87171" stopOpacity={0.08} />
              <stop offset={1} stopColor="#f87171" stopOpacity={0.45} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
          <XAxis dataKey="S" type="number" domain={["dataMin", "dataMax"]} tick={{ fill: "#64748b", fontSize: 10 }} tickCount={7} />
          <YAxis tick={{ fill: "#64748b", fontSize: 10 }} width={56} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
          <Tooltip contentStyle={{ background: "#0D121F", border: "1px solid #334155", fontSize: 11 }} labelFormatter={(v) => `NIFTY ${v}`}
            formatter={(v, n) => [rupee(v), n === "Today" ? "P&L today" : "P&L at expiry"]} />
          <ReferenceLine y={0} stroke="#475569" />
          {now && <ReferenceLine x={Math.round(now)} stroke="#818cf8" strokeDasharray="4 3" />}
          {bes.map((b) => <ReferenceLine key={b} x={b} stroke="#94a3b8" strokeDasharray="2 4" />)}
          <Area type="linear" dataKey="pnl" name="At expiry" stroke={light ? "#334155" : "#cbd5e1"} strokeWidth={1.5} fill={`url(#${gid})`} isAnimationActive={false} />
          {ivs && <Line type="monotone" dataKey="today" name="Today" stroke={light ? "#d97706" : "#fbbf24"} strokeWidth={1.5} strokeDasharray="5 3" dot={false} isAnimationActive={false} />}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export const PayoffToggle = ({ trade, spot, id }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3">
      <button data-testid={`${id}-toggle`} onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-[11px] font-semibold tracking-wide text-indigo-300 transition-colors hover:text-indigo-200">
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />} {open ? "Hide" : "Show"} payoff at expiry
      </button>
      {open && <div className="mt-2"><PayoffChart trade={trade} spot={spot} id={id} /></div>}
    </div>
  );
};
